/**
 * Typed bindings for /api/broadcasts — a prepared message posted into group
 * chats on a schedule — and the arithmetic the pages show about one.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatAccountNo } from "./accounts";
import { api } from "./api";
import type { LibraryItemKind } from "./library";
import type { Tone } from "./status";

export type BroadcastStatus = "active" | "paused";
/** rotate — accounts take turns in a chat; each — every account posts into every chat. */
export type BroadcastMode = "rotate" | "each";
export type MembershipState = "pending" | "requested" | "joined" | "blocked";
export type WaitingReason =
  | "no_messages"
  | "no_chats"
  | "no_accounts"
  | "accounts_blocked"
  | "resting"
  | "joining";

export interface BroadcastSchedule {
  mode: BroadcastMode;
  /** How often a chat gets a post (rotate) / an account posts into a chat (each). */
  interval_minutes: number;
  /** The pause between two posts of one account, drawn anew each time. */
  gap_min_seconds: number;
  gap_max_seconds: number;
  /** Before a new post the account removes its previous one in that chat. */
  delete_previous: boolean;
}

export interface BroadcastSummary extends BroadcastSchedule {
  id: string;
  name: string;
  status: BroadcastStatus;
  message_count: number;
  chat_count: number;
  /** Chats at least one account is in and may write to. */
  chats_ready: number;
  account_count: number;
  /** Accounts nothing holds back right now. */
  accounts_ready: number;
  posts_sent: number;
  posts_24h: number;
  /** Posts found gone on the next visit — taken down by the chat's side. */
  posts_removed: number;
  last_post_at: string | null;
  /** Why a running broadcast is not posting; null = nothing holds it. */
  waiting_reason: WaitingReason | null;
  created_at: string;
}

/** A library material the broadcast posts. */
export interface BroadcastMessage {
  id: string;
  kind: LibraryItemKind;
  title: string;
  text: string | null;
  file_name: string | null;
}

export interface BroadcastAccount {
  account_id: string;
  seq_no: number;
  label: string;
  /** Why the account is not posting right now (a code), null when it is. */
  blocker: string | null;
  posts_sent: number;
  last_post_at: string | null;
  next_free_at: string;
  chats_joined: number;
  chats_pending: number;
  chats_requested: number;
  chats_blocked: number;
}

export interface BroadcastMembership {
  account_id: string;
  state: MembershipState;
  reason: string | null;
  detail: string | null;
  not_before: string | null;
  posts_sent: number;
  last_post_at: string | null;
}

export interface BroadcastChat {
  id: string;
  title: string | null;
  username: string | null;
  by_invite: boolean;
  posts_sent: number;
  posts_removed: number;
  last_post_at: string | null;
  next_post_at: string | null;
  last_post_url: string | null;
  memberships: BroadcastMembership[];
}

export interface BroadcastDetail extends BroadcastSummary {
  messages: BroadcastMessage[];
  accounts: BroadcastAccount[];
  chats: BroadcastChat[];
  next_post_at: string | null;
}

/** An account as the "who posts" picker shows it. */
export interface AccountOption {
  id: string;
  seq_no: number;
  label: string;
  blocker: string | null;
}

export interface ChatsAdded {
  added: number;
  already: number;
  /** Lines naming a chat only by its number. */
  unsupported: string[];
  /** Lines with nothing recognisable in them. */
  skipped: number;
}

/** A group an account is already in, offered for adding. */
export interface AccountChat {
  tg_chat_id: number;
  title: string;
  username: string | null;
  members: number | null;
  already: boolean;
}

export interface BroadcastPost {
  id: string;
  chat_id: string;
  chat_title: string | null;
  account_label: string | null;
  status: "sent" | "failed";
  error: string | null;
  detail: string | null;
  sent_at: string;
  removed_at: string | null;
  url: string | null;
}

export interface BroadcastPostsPage {
  total: number;
  items: BroadcastPost[];
}

export interface CreateBroadcastPayload extends BroadcastSchedule {
  name: string;
  material_ids: string[];
  account_ids: string[];
  chats_text: string;
}

export const BROADCASTS_KEY = ["broadcasts"] as const;
const detailKey = (id: string | null) => ["broadcasts", id] as const;

const LIVE_REFRESH_MS = 30_000;

export function useBroadcasts() {
  return useQuery<BroadcastSummary[]>({
    queryKey: BROADCASTS_KEY,
    queryFn: () => api.get<BroadcastSummary[]>("/api/broadcasts"),
    // The worker posts on its own; without this the numbers froze on load.
    refetchInterval: (query) =>
      query.state.data?.some((b) => b.status === "active") ? LIVE_REFRESH_MS : false,
  });
}

export function useBroadcast(id: string | null) {
  return useQuery<BroadcastDetail>({
    queryKey: detailKey(id),
    enabled: id !== null,
    queryFn: () => api.get<BroadcastDetail>(`/api/broadcasts/${id}`),
    refetchInterval: (query) =>
      query.state.data?.status === "active" ? LIVE_REFRESH_MS : false,
  });
}

export function useAccountOptions(enabled = true) {
  return useQuery<AccountOption[]>({
    queryKey: ["broadcasts", "options", "accounts"],
    queryFn: () => api.get<AccountOption[]>("/api/broadcasts/options/accounts"),
    enabled,
    staleTime: 30_000,
  });
}

/** A change to one broadcast: afterwards the list and its card are re-read. */
function useBroadcastChange<TVars extends { id: string }, TOut>(
  run: (vars: TVars) => Promise<TOut>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: async (out, vars) => {
      // An endpoint that answers with the whole card saves the round trip.
      if (out && typeof out === "object" && "chats" in out && "accounts" in out) {
        // A read of the card sent before this change must not land on top of it.
        await qc.cancelQueries({ queryKey: detailKey(vars.id) });
        qc.setQueryData(detailKey(vars.id), out);
      } else {
        qc.invalidateQueries({ queryKey: detailKey(vars.id) });
      }
      qc.invalidateQueries({ queryKey: BROADCASTS_KEY, exact: true });
      qc.invalidateQueries({ queryKey: ["broadcast_posts", vars.id] });
    },
  });
}

export function useCreateBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBroadcastPayload) =>
      api.post<BroadcastDetail>("/api/broadcasts", payload),
    onSuccess: (created) => {
      qc.setQueryData(detailKey(created.id), created);
      qc.invalidateQueries({ queryKey: BROADCASTS_KEY, exact: true });
    },
  });
}

export function useUpdateBroadcast() {
  return useBroadcastChange(
    ({ id, ...patch }: { id: string; name?: string } & Partial<BroadcastSchedule>) =>
      api.patch<BroadcastDetail>(`/api/broadcasts/${id}`, patch),
  );
}

export function useBroadcastLifecycle(action: "pause" | "resume") {
  return useBroadcastChange(({ id }: { id: string }) =>
    api.post<BroadcastDetail>(`/api/broadcasts/${id}/${action}`),
  );
}

export function useDeleteBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/broadcasts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: BROADCASTS_KEY, exact: true }),
  });
}

export function useSetBroadcastMessages() {
  return useBroadcastChange(({ id, material_ids }: { id: string; material_ids: string[] }) =>
    api.put<BroadcastDetail>(`/api/broadcasts/${id}/messages`, { material_ids }),
  );
}

export function useSetBroadcastAccounts() {
  return useBroadcastChange(({ id, account_ids }: { id: string; account_ids: string[] }) =>
    api.put<BroadcastDetail>(`/api/broadcasts/${id}/accounts`, { account_ids }),
  );
}

export function useAddBroadcastChats() {
  return useBroadcastChange(({ id, text }: { id: string; text: string }) =>
    api.post<ChatsAdded>(`/api/broadcasts/${id}/chats`, { text }),
  );
}

export function useRemoveBroadcastChat() {
  return useBroadcastChange(({ id, chat_id }: { id: string; chat_id: string }) =>
    api.delete<void>(`/api/broadcasts/${id}/chats/${chat_id}`),
  );
}

export function useRetryBroadcastChat() {
  return useBroadcastChange(({ id, chat_id }: { id: string; chat_id: string }) =>
    api.post<void>(`/api/broadcasts/${id}/chats/${chat_id}/retry`),
  );
}

/** Read an account's own groups from Telegram, now — it takes a few seconds. */
export function useAccountChats() {
  return useMutation({
    mutationFn: ({ id, account_id }: { id: string; account_id: string }) =>
      api.post<AccountChat[]>(`/api/broadcasts/${id}/account-chats`, { account_id }),
  });
}

export function useAddChatsFromAccount() {
  return useBroadcastChange(
    ({ id, ...body }: { id: string; account_id: string; tg_chat_ids: number[] }) =>
      api.post<ChatsAdded>(`/api/broadcasts/${id}/chats/from-account`, body),
  );
}

/** Send a material to the account's own Saved Messages, to look at it. */
export function useTestSend() {
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; account_id: string; material_id: string }) =>
      api.post<void>(`/api/broadcasts/${id}/test-send`, body),
  });
}

export function useBroadcastPosts(
  id: string | null,
  filter: { only?: "failed" | "removed"; limit?: number } = {},
  live = false,
) {
  return useQuery<BroadcastPostsPage>({
    queryKey: ["broadcast_posts", id, filter],
    enabled: id !== null,
    refetchInterval: live ? LIVE_REFRESH_MS : false,
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(filter.limit ?? 50) });
      if (filter.only) params.set("only", filter.only);
      return api.get<BroadcastPostsPage>(`/api/broadcasts/${id}/posts?${params}`);
    },
  });
}

/* --------------------------------------------------------------- the accounts */

/** "#064 · name" — the way an account is named on the accounts page. */
export function accountName(account: { seq_no: number; label: string }): string {
  return `${formatAccountNo(account.seq_no)} · ${account.label}`;
}

/** Reasons that pass on their own; the rest need the operator. */
const PASSING = new Set(["resting", "warming_up", "flood_wait", "peer_flood", "pending_verify"]);

export function blockerTone(blocker: string | null): Tone {
  if (blocker === null) return "success";
  return PASSING.has(blocker) ? "warning" : "danger";
}

/* ------------------------------------------------------------------ the speed */

export type SpeedPreset = "careful" | "normal" | "fast";

type Pace = Pick<BroadcastSchedule, "interval_minutes" | "gap_min_seconds" | "gap_max_seconds">;

/**
 * Three ready paces. "normal" is what such tools ship as their default for a
 * young account; "fast" is what they advise only for an old one.
 */
export const SPEED_PRESETS: Record<SpeedPreset, Pace> = {
  careful: { interval_minutes: 240, gap_min_seconds: 300, gap_max_seconds: 600 },
  normal: { interval_minutes: 120, gap_min_seconds: 180, gap_max_seconds: 300 },
  fast: { interval_minutes: 60, gap_min_seconds: 60, gap_max_seconds: 120 },
};

export const DEFAULT_SCHEDULE: BroadcastSchedule = {
  mode: "rotate",
  ...SPEED_PRESETS.normal,
  delete_previous: false,
};

/** The server's floors and ceilings (app.core.broadcast). */
export const SCHEDULE_LIMITS = {
  interval_minutes: { min: 15, max: 7 * 24 * 60 },
  gap_seconds: { min: 30, max: 3600 },
} as const;

/** Which preset these numbers are, if any. */
export function presetOf(pace: Pace): SpeedPreset | null {
  const names = Object.keys(SPEED_PRESETS) as SpeedPreset[];
  return (
    names.find((name) => {
      const p = SPEED_PRESETS[name];
      return (
        p.interval_minutes === pace.interval_minutes &&
        p.gap_min_seconds === pace.gap_min_seconds &&
        p.gap_max_seconds === pace.gap_max_seconds
      );
    }) ?? null
  );
}

/** An account sleeps eight hours a night, each at its own time. */
const AWAKE_HOURS = 16;
/** Joins an account makes in a day (the fleet-wide join budget). */
export const JOINS_PER_DAY = 10;

export interface Forecast {
  /** Whether the accounts can keep the interval at this pause. */
  keepsUp: boolean;
  /** How often a chat really gets a post from one sender, minutes. */
  realIntervalMinutes: number;
  /** Posts one account makes in an hour it is awake, and in a day. */
  perAccountPerHour: number;
  perAccountPerDay: number;
  /** Posts landing in one chat in a day, from all accounts together. */
  perChatPerDay: number;
  /**
   * Accounts that would keep the interval when taking turns. Null when each
   * account posts everywhere: more accounts then carry more work, not less.
   */
  accountsNeeded: number | null;
}

/**
 * What a schedule comes to in practice. An account can post only so often —
 * once per pause — so past a point more chats or a shorter interval just
 * stretch the real interval. Null when there is nothing to count yet.
 */
export function forecast(
  schedule: Pace & Pick<BroadcastSchedule, "mode">,
  chats: number,
  accounts: number,
): Forecast | null {
  if (chats <= 0 || accounts <= 0) return null;
  const pause = (schedule.gap_min_seconds + schedule.gap_max_seconds) / 2;
  const capacityPerHour = (accounts * 3600) / pause;
  const slots = schedule.mode === "rotate" ? chats : chats * accounts;
  const wantedPerHour = (slots * 60) / schedule.interval_minutes;
  const actualPerHour = Math.min(wantedPerHour, capacityPerHour);
  const stretch = Math.max(1, wantedPerHour / capacityPerHour);
  return {
    keepsUp: wantedPerHour <= capacityPerHour,
    realIntervalMinutes: schedule.interval_minutes * stretch,
    perAccountPerHour: actualPerHour / accounts,
    perAccountPerDay: (actualPerHour / accounts) * AWAKE_HOURS,
    perChatPerDay: (actualPerHour / chats) * AWAKE_HOURS,
    accountsNeeded:
      schedule.mode === "rotate" ? Math.ceil((wantedPerHour * pause) / 3600) : null,
  };
}

/** Days for one account to get into this many chats. */
export function joinDays(chats: number): number {
  return Math.ceil(Math.max(0, chats) / JOINS_PER_DAY);
}

/**
 * The chats a pasted text names, the way the server will read it: a t.me link,
 * an @username or an invite link per line — the first on the line counts, the
 * rest of it is ignored. For the count under the field; the server decides.
 */
export function countChatLines(text: string): number {
  const seen = new Set<string>();
  for (const line of text.split("\n")) {
    const invite = /(?:t(?:elegram)?\.me\/)?(?:joinchat\/|\+)([A-Za-z0-9_-]{8,})/i.exec(line);
    if (invite) {
      seen.add(`+${invite[1]}`);
      continue;
    }
    const link = /t(?:elegram)?\.me\/@?([A-Za-z][A-Za-z0-9_]{4,31})/i.exec(line);
    const handle = link ?? /@([A-Za-z][A-Za-z0-9_]{4,31})\b/.exec(line);
    if (handle) seen.add(handle[1].toLowerCase());
  }
  return seen.size;
}
